/**
 * ElementToolbarPlugin provides a floating toolbar that appears when a user selects
 * an element in the editor that has associated tools. The toolbar is positioned using
 * Popper.js and dynamically shows/hides based on editor focus and element selection.
 *
 * Key features:
 * - Automatically detects elements with available tools
 * - Positions toolbar relative to selected element using Popper.js
 * - Handles scroll containers and overflow prevention
 * - Supports modal dialogs for complex tool interactions
 * - Optimizes for performance by avoiding unnecessary re-renders
 */

import { ApplicationView, observable, observe, raw } from '@aha-app/mvc';
import { createPopper, type Instance, type Padding } from '@popperjs/core';
import Tippy, { useSingleton } from '@tippyjs/react';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Editor, Element, type PathRef, type NodeEntry } from 'slate';
import { ReactEditor, useReadOnly } from 'slate-react';
import type AhaEditor from '../../AhaEditor';
import styles from '../../components/ElementToolbar/ElementToolbar.module.css';
import RenderTools from '../../components/ElementToolbar/RenderTools';
import ToolbarModalContainer from '../../components/ElementToolbar/ToolbarModalContainer';
import { EDITOR_TOOLBAR_HEIGHT } from '../../constants';
import type AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import {
  type CustomElement,
  type EditorPluginFactory,
  type LayoutComponent,
  type ToolbarItems,
} from '../../types/aha-editor';
import findScrollContainer from '../../util/findScrollContainer';
import { cancelEventHandlersOutsideFocusableElements } from '../../util/mouseHandlers';
import type Tool from '../../tools/Tool';
import { isEqual } from 'lodash';
import runAfterSlate from '../../util/runAfterSlate';

// We can't actually know which elements have tools. This type might be pointless :shrug:
type ElementWithTools = Extract<CustomElement, { type: CustomElement['type'] }>;

interface VisibleToolbarState {
  showToolbar: true;
  elementEntry: NodeEntry<ElementWithTools>;
  toolbarItems: ToolbarItems;
  modalTool?: typeof Tool;
}

type ElementToolbarState = { showToolbar: false } | VisibleToolbarState;

const ElementToolbarComponent: EditorPluginFactory = () => {
  // Used as a matcher to find an element that has tools
  function elementHasTools(element: Element): element is ElementWithTools {
    return (
      Element.isElement(element) &&
      ahaEditor.elementToolbarItems.has(element.type)
    );
  }

  // Used to set up the popper config. We run this whenever one of the reference element changes.
  function updatePopperElement(referenceElement?: HTMLElement) {
    // If popper doesn't exist, the editor might've been unmounted
    if (!popper) {
      return;
    }

    // Update element first, then reset options
    const currentReferenceElement = instance?.state.elements.reference;
    if (referenceElement && currentReferenceElement !== referenceElement) {
      if (instance) {
        instance.destroy();
      }

      // We need to re-create the instance
      instance = createPopper(referenceElement, popper);
    }

    updatePopperConfig();
  }

  // Run this whenever any of the popper settings change (scrollContainer, padding)
  function updatePopperConfig() {
    instance?.setOptions({
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            tether: false,
            altAxis: true,
            boundary: scrollContainer,
            padding,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: [],
          },
        },
        {
          name: 'offset',
          options: {
            // To keep the toolbar from touching the bottom of the screen when scrolling.
            offset: [0, 15],
          },
        },
      ],
    });
  }

  let ahaEditor: AhaEditor;
  let controller: AhaEditorV2Controller;

  // We store a pathRef in order to avoid re-opening the toolbar if it's the same element
  // For example, if a collaborative user adds a block above the selected element, the path will change,
  // and trigger the onChange event. However, it's the same element, so we don't want to re-render/re-open the toolbar.
  // This is primarily an issue if using a tool "edit" model: e.g. editing the URL of a link.
  let pathRef: PathRef | undefined;

  // The local plugin state for the toolbar
  const toolbarState = observable<ElementToolbarState>({
    showToolbar: false,
  });

  // Popper references
  let scrollContainer: HTMLElement | null = null;
  let padding: Padding = {};

  // Container for the popper content
  let popper: HTMLDivElement | undefined = document.createElement('div');
  popper.setAttribute('popover', 'manual');

  // Sometimes the content of the toolbar will resize, let's update the popper when that happens
  const observer = new ResizeObserver(() => {
    instance?.update();
  });
  observer.observe(popper);

  let instance: Instance | undefined;

  const showToolbarModal = (tool: typeof Tool) => {
    if (!toolbarState.showToolbar) {
      return;
    }
    toolbarState.modalTool = tool;
  };

  const hideToolbarModal = () => {
    if (!toolbarState.showToolbar) {
      return;
    }

    toolbarState.modalTool = undefined;
  };

  const ToolbarWrapper: LayoutComponent = () => {
    const readOnly = useReadOnly();
    const [tippySingletonSource, tippySingletonTarget] = useSingleton();

    const showToolbar =
      toolbarState.showToolbar && !readOnly && !!toolbarState.toolbarItems;
    const element = showToolbar ? raw(toolbarState.elementEntry[0]) : undefined;

    // It's possible the element type has changed, e.g. from <p> to <pre>
    // We need to re-run the toolbar state after render, since the `<p>` tag will be the current node
    // returned by ReactEditor.toDOMNode on the initial render.
    useEffect(() => {
      updateToolbarState(false);
    }, [element?.type]);

    let content: React.ReactNode = null;
    if (showToolbar) {
      const { modalTool } = toolbarState;

      let toolbarContent: React.ReactNode;
      if (modalTool) {
        toolbarContent = (
          <ToolbarModalContainer
            modalTool={raw(modalTool)}
            editor={ahaEditor.editor}
            element={raw(toolbarState.elementEntry[0])}
            toolbarShowModal={showToolbarModal}
            toolbarHideModal={hideToolbarModal}
          />
        );
      } else {
        toolbarContent = (
          <RenderTools
            controller={raw(controller)}
            toolbarTools={raw(toolbarState.toolbarItems)}
            editor={ahaEditor.editor}
            element={raw(toolbarState.elementEntry[0])}
            toolbarShowModal={showToolbarModal}
            toolbarHideModal={hideToolbarModal}
            tippySingleton={tippySingletonTarget}
          />
        );
      }

      content = (
        <div
          data-element-toolbar
          className={classNames(
            styles.elementToolbar,
            styles.elementToolbarCommon
          )}
          // Interactions within the editor should never close the drawer.
          data-skip-drawer-close
          {...cancelEventHandlersOutsideFocusableElements}
        >
          {toolbarContent}
        </div>
      );
    }

    return (
      <>
        <Tippy
          singleton={tippySingletonSource}
          placement="top"
          delay={500}
          zIndex={12000}
        />

        {popper && ReactDOM.createPortal(content, popper)}
      </>
    );
  };

  function hideToolbar() {
    Object.assign(toolbarState, {
      showToolbar: false,
      element: undefined,
      toolbarItems: undefined,
      modalTool: undefined,
    });

    instance?.update();

    if (popper?.isConnected) {
      popper.hidePopover();
    }
  }

  function showToolbar(entry: NodeEntry<ElementWithTools>) {
    if (toolbarState.showToolbar) {
      // If we're already showing the toolbar for this element, bail out
      if (
        raw(toolbarState.elementEntry[0]) === entry[0] ||
        (pathRef && isEqual(entry[1], pathRef.current))
      ) {
        toolbarState.elementEntry = entry;

        // In case of collaborative changes, we want to move the toolbar around after Slate has fully synced updates to the DOM.
        // This is to keep the position of the toolbar in line with the element, if it's being moved around.
        runAfterSlate(() => {
          instance?.update();
        });
        return;
      }
    }

    // In theory this should never happen, since the element should always have tools
    // Just an extra safety guard, and also appeases TS
    const toolbarItems = ahaEditor.elementToolbarItems.get(entry[0].type);
    if (!toolbarItems) {
      hideToolbar();
      return;
    }

    /**
     * Tools can define a static `openToolbarModal` method. If it returns `true`, we will
     * automatically open that tool's modal on first render. This is used, e.g. to open
     * the "Edit link" modal when first inserting a new link w/o a URL.
     *
     * We only check for `openToolbarModal` when the toolbar is first shown (currently hidden),
     * or if the currently selected element is different.
     */
    let modalTool: VisibleToolbarState['modalTool'];
    if (
      !toolbarState.showToolbar ||
      entry[0] !== raw(toolbarState.elementEntry[0])
    ) {
      modalTool = toolbarItems
        .flat(10)
        .find((tool) =>
          tool.openToolbarModal({ editor: ahaEditor.editor, element: entry[0] })
        );
    }

    if (pathRef) {
      // Always call .unref() when done with a PathRef
      pathRef.unref();
    }
    pathRef = Editor.pathRef(ahaEditor.editor, entry[1]);

    Object.assign(toolbarState, {
      showToolbar: true,
      elementEntry: entry,
      toolbarItems,
      modalTool,
    });

    window.requestAnimationFrame(() => {
      instance?.update();

      if (popper?.isConnected) {
        popper.showPopover();
      }
    });
  }

  function updateToolbarState(retry = true) {
    const {
      editor: { selection },
    } = ahaEditor;

    const { focused, isResizing, readOnly } = controller.state;

    if (!selection || !focused || isResizing || readOnly) {
      hideToolbar();
      return;
    }

    try {
      // Get the nearest common ancestor element that has toolbar items
      const elementWithTools = Editor.above(ahaEditor.editor, {
        match: elementHasTools,
      });

      if (elementWithTools) {
        // Get the DOM element for the Slate element
        const element = ReactEditor.toDOMNode(
          ahaEditor.editor,
          elementWithTools[0]
        );

        updatePopperElement(element);

        if (element) {
          showToolbar(elementWithTools);
          return;
        }
      }
    } catch (ex) {
      // Error finding the element or DOM node, it might be because the node was just added. Retry just once, but after a delay.
      if (retry) {
        runAfterSlate(() => updateToolbarState(false));
      }

      return;
    }

    // If we didn't find an element/DOM node, hide the toolbar
    hideToolbar();
  }

  return {
    name: 'Element toolbar component',
    layoutComponent: {
      Component: ApplicationView(ToolbarWrapper),
      location: 'below',
    },
    handlers: {
      onInit(_ahaEditor) {
        ahaEditor = _ahaEditor;
        controller = ahaEditor.controller;
      },

      onMount() {
        // Mount the popper into the toolbar container
        if (popper) {
          ahaEditor.controller.editorContainer.appendChild(popper);
        }

        padding = {
          ...(controller.props.elementToolbarOptions?.padding ?? {}),
        };

        if (!('top' in padding)) {
          padding.top = EDITOR_TOOLBAR_HEIGHT + 10;
        }

        // These observers watch for Slate/Editor state and will update the toolbar state, rather than forcing re-renders on all state changes.
        const observers = [
          observe(() => {
            // updateToolbarState reads values from state, so it's automatically observing changes
            updateToolbarState();
          }),

          // Change the scroll container based on the full screen state
          observe(() => {
            scrollContainer = controller.state.pluginState.isFullScreen
              ? controller.editorContainer
              : controller.editorContainer &&
                findScrollContainer(controller.editorContainer);

            updatePopperConfig();
          }),
        ];

        return () => {
          observers.forEach((observer) => observer());
        };
      },

      onChange() {
        updateToolbarState();
      },

      onDestroy() {
        instance?.destroy();
        instance = undefined;

        observer.disconnect();

        if (popper?.isConnected) {
          popper.hidePopover();
          popper.remove();
          popper = undefined;
        }
      },
    },
  };
};

const ElementToolbarPlugin = [ElementToolbarComponent];

export default ElementToolbarPlugin;
